import { Box, useTheme, alpha } from '@mui/material'
import { memo } from 'react'
import './style.css'

export type StrikeTooltipOiBarAlign = 'start' | 'end'

export type StrikeTooltipOiBarProps = {
    oiRank: number
    openInterest: number
    /** OI change (`current − previous`) from chain payload */
    oiChangeRaw: number | undefined
    /** `start` = grow from left (puts toward strike); `end` = grow from right (calls toward strike) */
    align?: StrikeTooltipOiBarAlign
    className?: string
}

type OiSegment = {
    key: string
    widthPctOfTrack: number
    className: string
    sx: Record<string, unknown>
}

const mirrorBorderRadius = (radius: unknown): unknown => {
    if (radius === '2px') {
        return '0 2px 2px 0'
    }
    if (radius === '2px 0 0 2px') {
        return '0 2px 2px 0'
    }
    if (radius === '0 2px 2px 0') {
        return '2px 0 0 2px'
    }
    return radius
}

const mirrorSegmentSx = (sx: Record<string, unknown>): Record<string, unknown> => {
    const next = { ...sx, borderRadius: mirrorBorderRadius(sx.borderRadius) }
    if ('borderRight' in next) {
        next.borderLeft = next.borderRight
        delete next.borderRight
    }
    return next
}

const StrikeTooltipOiBar = memo(({ oiRank, openInterest: curr, oiChangeRaw, align = 'start', className }: StrikeTooltipOiBarProps) => {
    const mirrored = align === 'end'
    const theme = useTheme()
    const delta = oiChangeRaw ?? 0
    const pctCurrVsMax = Math.min(100, Math.max(0, oiRank))
    const prevOi = curr - delta

    const secondary = alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.75 : 0.65)
    const stripeFill = alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.35 : 0.28)
    const stripeAccent = alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.62 : 0.52)
    const trackTint = alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.85 : 0.9)
    const voidBorder = alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.5 : 0.45)

    const trackClassName = ['strike-tooltip-oi-track', className].filter(Boolean).join(' ')

    if (curr <= 0 || pctCurrVsMax <= 0) {
        return <Box className={trackClassName} sx={{ backgroundColor: trackTint }} />
    }

    const segments: OiSegment[] = []

    if (delta > 0 && curr > 0) {
        const clampedPrevOi = Math.max(0, prevOi)
        const wPrevVsMax = Math.min(pctCurrVsMax, (clampedPrevOi / curr) * pctCurrVsMax)
        const wStripeVsMax = Math.max(0, pctCurrVsMax - wPrevVsMax)
        segments.push({
            key: 'prev-vs-max',
            widthPctOfTrack: wPrevVsMax,
            className: 'strike-tooltip-oi-seg strike-tooltip-oi-seg-solid',
            sx: {
                backgroundColor: secondary,
                borderRadius: wStripeVsMax > 1e-6 ? '2px 0 0 2px' : '2px',
            },
        })
        if (wStripeVsMax > 1e-6) {
            segments.push({
                key: 'delta-vs-max',
                widthPctOfTrack: wStripeVsMax,
                className: 'strike-tooltip-oi-seg strike-tooltip-oi-seg-striped',
                sx: {
                    borderRadius: '0 2px 2px 0',
                    backgroundColor: stripeFill,
                    backgroundImage: `repeating-linear-gradient(-45deg, ${stripeAccent}, ${stripeAccent} 2px, transparent 2px, transparent 4px)`,
                },
            })
        }
    } else if (delta < 0 && prevOi > curr && curr > 0) {
        const dropVsOi = prevOi - curr
        const wVoidVsMax = (pctCurrVsMax / curr) * dropVsOi
        segments.push({
            key: 'curr-vs-max',
            widthPctOfTrack: pctCurrVsMax,
            className: 'strike-tooltip-oi-seg strike-tooltip-oi-seg-solid',
            sx: {
                backgroundColor: secondary,
                borderRadius: wVoidVsMax > 1e-6 ? '2px 0 0 2px' : '2px',
            },
        })
        if (wVoidVsMax > 1e-6) {
            segments.push({
                key: 'void-vs-max',
                widthPctOfTrack: wVoidVsMax,
                className: 'strike-tooltip-oi-seg strike-tooltip-oi-seg-void',
                sx: {
                    borderRadius: '0 2px 2px 0',
                    backgroundColor: 'transparent',
                    boxSizing: 'border-box',
                    borderTop: `0.5px solid ${voidBorder}`,
                    borderBottom: `0.5px solid ${voidBorder}`,
                    borderRight: `0.5px solid ${voidBorder}`,
                },
            })
        }
    } else {
        segments.push({
            key: 'curr-flat',
            widthPctOfTrack: pctCurrVsMax,
            className: 'strike-tooltip-oi-seg strike-tooltip-oi-seg-solid',
            sx: {
                borderRadius: '2px',
                backgroundColor: secondary,
            },
        })
    }

    const totalWidthPct = segments.reduce((sum, seg) => sum + seg.widthPctOfTrack, 0)
    const toGroupPct = (trackPct: number) =>
        totalWidthPct > 1e-6 ? `${(trackPct / totalWidthPct) * 100}%` : '0%'

    const segmentNodes = segments.map((seg) => {
        const segSx = mirrored ? mirrorSegmentSx(seg.sx) : seg.sx
        return (
            <Box
                key={seg.key}
                className={seg.className}
                sx={{
                    ...segSx,
                    width: mirrored ? toGroupPct(seg.widthPctOfTrack) : `${seg.widthPctOfTrack}%`,
                }}
            />
        )
    })

    if (mirrored) {
        return (
            <Box className={trackClassName} sx={{ backgroundColor: trackTint }}>
                <Box sx={{ flex: 1, minWidth: 0 }} />
                <Box
                    className="strike-tooltip-oi-filled strike-tooltip-oi-filled--mirrored"
                    sx={{
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        flexWrap: 'nowrap',
                        width: `${totalWidthPct}%`,
                        height: '100%',
                        minWidth: 0,
                        flexShrink: 0,
                    }}
                >
                    {segmentNodes}
                </Box>
            </Box>
        )
    }

    return (
        <Box className={trackClassName} sx={{ backgroundColor: trackTint }}>
            {segmentNodes}
        </Box>
    )
})

export default StrikeTooltipOiBar
